import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileCheck2, Loader2, Plus, Send, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { PaisesPicker } from "@/components/PaisesPicker";
import { TitularPicker } from "@/components/TitularPicker";
import { ResponsablePicker } from "@/components/ResponsablePicker";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  Input,
  PageContainer,
  PageHeader,
  Textarea,
} from "@/components/ui";

/**
 * Alta de una solicitud de Carta de Opinión Técnica (#17) — formato
 * NPM-T0036-E6.
 *
 * Trámite PRE-certificación: no parte de un certificado vigente. NPMX emite
 * una opinión técnica sobre 1..10 modelos (marca, modelo, fabricante) de
 * productos de telecomunicaciones; la unidad de análisis es el MODELO.
 *
 * Diseño funcional: Insumos/CartaDeOpinionTecnica/
 * diseno-funcional-carta-opinion-tecnica-telecom-sicen-npmx.md
 */

interface Norma { id: string; codigo: string; titulo: string; tipo: string; }
interface Pais { id: string; codigo: string; nombre: string; }
interface Titular { id: string; razonSocial: string; rfc: string; estatus: string; }
interface ContextoMio {
  titularId: string;
  titularRazonSocial: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  titularPuedeActuarComoTitular: boolean;
  titularPuedeActuarComoTramitador: boolean;
}
interface ModeloRow { marca: string; modelo: string; fabricante: string; }

const CONTEXTO_TIPO = "SOLICITUD_TELECOM";
const MAX_MODELOS = 10;
const IVA = 0.16;

// Declaración del formato NPM-T0036-E6 (bloque FIRMA).
const DECLARACION =
  "Declaro, bajo protesta de decir verdad, que los datos asentados en esta solicitud y en " +
  "los requisitos anexos que se adjuntan son verdaderos y manifiesto ser la persona " +
  "responsable de dar respuesta a averiguaciones relacionadas con la presente solicitud y " +
  "de proveer muestras para el seguimiento posterior al otorgamiento de la certificación, " +
  "de ser el caso.";

// Requisitos del alta (NPM-T0039-E3); el anexo es opcional. La obligatoriedad
// se avisa en UI y el bloqueo duro queda del lado del evaluador (consistente
// con los demás servicios Telecom).
const DOCS_REQUERIDOS: Record<string, string> = {
  CARTA_OT_ESPECIFICACIONES: "Especificaciones técnicas del producto",
  CARTA_OT_MANUAL: "Instructivo o manual del producto",
  CARTA_OT_FOLLETOS_FOTOS: "Folletos, fotografías o imágenes del producto",
};

const MODELO_VACIO: ModeloRow = { marca: "", modelo: "", fabricante: "" };

export function OpinionTecnicaAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const titularIdInicial =
    params.get("titularId") ?? window.sessionStorage.getItem("sicen.contexto.titularId");

  // ----- Estado del formulario ------------------------------------------------
  const [titular, setTitular] = useState<Titular | null>(null);
  const [normasSel, setNormasSel] = useState<string[]>([]);
  const [productoNombre, setProductoNombre] = useState("");
  const [modelos, setModelos] = useState<ModeloRow[]>([{ ...MODELO_VACIO }]);
  const [accesorios, setAccesorios] = useState("S/A");
  const [paisesOrigen, setPaisesOrigen] = useState<string[]>([]);
  const [paisesProcedencia, setPaisesProcedencia] = useState<string[]>([]);
  const [fraccionesText, setFraccionesText] = useState("");
  const [equipoNoNuevo, setEquipoNoNuevo] = useState(false);
  const [representante, setRepresentante] = useState("");
  const [declaracion, setDeclaracion] = useState(false);
  const [observaciones, setObservaciones] = useState("");
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);

  // ----- Contexto (titular/tramitador desde Registro Legal) --------------------
  const qContextos = useQuery<ContextoMio[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<ContextoMio[]>("/api/contextos/mios")).data,
    enabled: access.allowed,
  });
  const ctxActivo = qContextos.data?.find((c) => c.titularId === titularIdInicial) ?? null;
  const ctxEsTramitadorPuro =
    !!ctxActivo && !ctxActivo.titularPuedeActuarComoTitular && ctxActivo.titularPuedeActuarComoTramitador;
  const esExterno = !access.esInterno;
  const titularBloqueado = esExterno && !!ctxActivo?.titularPuedeActuarComoTitular;

  useEffect(() => {
    if (!ctxActivo || titular) return;
    if (ctxActivo.titularPuedeActuarComoTitular) {
      setTitular({ id: ctxActivo.titularId, razonSocial: ctxActivo.titularRazonSocial, rfc: "", estatus: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxActivo]);

  const tramitadorId = ctxActivo?.tramitadorId ?? (ctxEsTramitadorPuro ? ctxActivo?.titularId : null);
  const tramitadorNombre =
    ctxActivo?.tramitadorRazonSocial ?? (ctxEsTramitadorPuro ? ctxActivo?.titularRazonSocial : null);

  // ----- Catálogos --------------------------------------------------------------
  const qCatalogos = useQuery<{ normas: Norma[]; costo: number; maxModelos: number }>({
    queryKey: ["telecom-opinion-tecnica-catalogos"],
    queryFn: async () =>
      (await api.get<{ normas: Norma[]; costo: number; maxModelos: number }>(
        "/api/sector/telecom/opinion-tecnica/catalogos",
      )).data,
    enabled: access.allowed,
  });
  const qPaises = useQuery<Pais[]>({
    queryKey: ["telecom-paises"],
    queryFn: async () => (await api.get<Pais[]>("/api/sector/telecom/paises")).data,
    enabled: access.allowed,
  });
  const maxModelos = qCatalogos.data?.maxModelos ?? MAX_MODELOS;

  // Cálculo conservado a propósito: la visualización del costo está oculta
  // por ajuste de UX (doc 202605291014). La tarifa incluye hasta 10 modelos.
  const totalCosto = useMemo(() => {
    const subtotal = qCatalogos.data?.costo ?? 1529;
    return { subtotal, iva: subtotal * IVA, total: subtotal * (1 + IVA) };
  }, [qCatalogos.data?.costo]);
  void totalCosto;

  // ----- Validación (RN-02…RN-08 en su parte de captura) -------------------------
  const fracciones = useMemo(
    () => fraccionesText.split(",").map((f) => f.trim()).filter(Boolean),
    [fraccionesText],
  );
  // RN-05: 8 dígitos + NICO opcional a 2.
  const fraccionesInvalidas = fracciones.filter((f) => !/^\d{8}(\d{2})?$/.test(f));

  const modelosCompletos = modelos.every(
    (m) => m.marca.trim() !== "" && m.modelo.trim() !== "" && m.fabricante.trim() !== "",
  );
  // RN-04: sin duplicar la tupla (marca, modelo).
  const modelosDuplicados = useMemo(() => {
    const vistos = new Set<string>();
    const dups: string[] = [];
    for (const m of modelos) {
      const clave = `${m.marca.trim()}|${m.modelo.trim()}`.toLowerCase();
      if (m.marca.trim() && m.modelo.trim() && !vistos.add(clave)) {
        dups.push(`${m.marca.trim()} ${m.modelo.trim()}`);
      }
    }
    return dups;
  }, [modelos]);

  const requeridosPendientes = Object.entries(DOCS_REQUERIDOS)
    .filter(([codigo]) => !docs.some((d) => d.tipoCodigo === codigo))
    .map(([, nombre]) => nombre);

  const valido =
    !!titular &&
    normasSel.length > 0 &&
    productoNombre.trim() !== "" &&
    modelos.length >= 1 &&
    modelos.length <= maxModelos &&
    modelosCompletos &&
    modelosDuplicados.length === 0 &&
    paisesOrigen.length > 0 &&
    paisesProcedencia.length > 0 &&
    fracciones.length > 0 &&
    fraccionesInvalidas.length === 0 &&
    representante.trim() !== "" &&
    declaracion;

  const enviar = async () => {
    if (!valido || !titular) return;
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        id = (
          await api.post<{ id: string }>("/api/sector/telecom/solicitudes/opinion-tecnica", {
            titularId: titular.id,
            tramitadorId: tramitadorId ?? null,
            normasIds: normasSel,
            productoNombre: productoNombre.trim(),
            modelos: modelos.map((m) => ({
              marca: m.marca.trim(),
              modelo: m.modelo.trim(),
              fabricante: m.fabricante.trim(),
            })),
            accesorios: accesorios.trim() || "S/A",
            paisesOrigenIds: paisesOrigen,
            paisesProcedenciaIds: paisesProcedencia,
            fracciones,
            equipoNoNuevo,
            representanteNombre: representante.trim(),
            declaracionAceptada: declaracion,
            observaciones: observaciones.trim() || null,
          })
        ).data.id;
        setSolicitudCreada(id);
      }
      const fallidos = docs.length
        ? await subirDocumentosCapturados(docs, setDocs, CONTEXTO_TIPO, id)
        : 0;
      if (fallidos > 0) {
        setError(
          `La solicitud se registró, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el detalle de la solicitud.`,
        );
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.",
      );
      setEnviando(false);
    }
  };

  const setModelo = (idx: number, campo: keyof ModeloRow, valor: string) =>
    setModelos(modelos.map((m, i) => (i === idx ? { ...m, [campo]: valor } : m)));
  const quitarModelo = (idx: number) => setModelos(modelos.filter((_, i) => i !== idx));

  // ----- Guards -----------------------------------------------------------------
  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios"
        backLabel="Menú de servicios"
        icon={<FileCheck2 className="h-6 w-6" />}
        title="Solicitud de Carta de Opinión Técnica"
        subtitle="Formato NPM-T0036-E6 · Servicio #17 · Cartas y Anexos"
      />

      <Alert variant="info" title="¿Qué es la Carta de Opinión Técnica?">
        Opinión de NPMX sobre la aplicabilidad de las normas y disposiciones técnicas a uno o
        más <strong>modelos de producto</strong> (hasta {maxModelos} por solicitud). Es un
        trámite previo a la certificación y no requiere certificado en SICEN.
      </Alert>

      {/* Solicitante */}
      <Card title="Solicitante">
        {titularBloqueado || (titular && esExterno) ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <DataField label="Razón social (titular)" value={titular?.razonSocial ?? "—"} />
            {tramitadorNombre && <DataField label="Tramitador" value={tramitadorNombre} />}
          </div>
        ) : (
          <FormField
            label="Empresa titular"
            required
            hint="Busca la empresa cliente titular del producto."
          >
            <TitularPicker value={titular} onChange={setTitular} />
          </FormField>
        )}
        <p className="mt-3 text-xs text-fg-subtle">
          Datos precargados desde Registro Legal. Si cambiaron personas o circunstancias,
          actualiza el Registro Legal antes de enviar la solicitud.
        </p>
      </Card>

      {/* Norma */}
      <Card title="Norma / Disposición Técnica">
        <p className="mb-3 text-sm text-fg-subtle">
          Selecciona la(s) NOM/DT del formato aplicables al producto. Mínimo una.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {(qCatalogos.data?.normas ?? []).map((n) => (
            <label
              key={n.id}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-sm hover:bg-surface-2"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={normasSel.includes(n.id)}
                onChange={() =>
                  setNormasSel(
                    normasSel.includes(n.id)
                      ? normasSel.filter((x) => x !== n.id)
                      : [...normasSel, n.id],
                  )
                }
              />
              <span>
                <span className="font-medium">{n.codigo}</span>
                <span className="block text-xs text-fg-subtle">{n.titulo}</span>
              </span>
            </label>
          ))}
        </div>
      </Card>

      {/* Producto */}
      <Card title="Datos del producto">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Nombre del producto" required className="sm:col-span-2">
            <Input value={productoNombre} onChange={(e) => setProductoNombre(e.target.value)}
                   placeholder="p. ej. Teléfono inteligente (Smartphone)" />
          </FormField>
          <FormField label="Accesorios" hint="«S/A» cuando no aplica.">
            <Input value={accesorios} onChange={(e) => setAccesorios(e.target.value)} />
          </FormField>
          <FormField
            label="Fracción(es) arancelaria(s)"
            required
            hint="8 dígitos + NICO opcional a 2, separadas por coma. La fracción correcta es responsabilidad del usuario."
            error={fraccionesInvalidas.length > 0 ? `Inválidas: ${fraccionesInvalidas.join(", ")}` : undefined}
          >
            <Input
              value={fraccionesText}
              onChange={(e) => setFraccionesText(e.target.value)}
              placeholder="p. ej. 85171300, 8517130001"
            />
          </FormField>
          <div className="sm:col-span-2 grid gap-4 sm:grid-cols-2">
            <PaisesPicker
              label="País(es) de origen"
              required
              opciones={qPaises.data ?? []}
              seleccion={paisesOrigen}
              onChange={setPaisesOrigen}
            />
            <PaisesPicker
              label="País(es) de procedencia"
              required
              opciones={qPaises.data ?? []}
              seleccion={paisesProcedencia}
              onChange={setPaisesProcedencia}
            />
          </div>
          <label className="flex items-start gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={equipoNoNuevo}
              onChange={(e) => setEquipoNoNuevo(e.target.checked)}
            />
            <span>
              El equipo <strong>no es nuevo</strong> (reacondicionado; transitorio quinto del
              PEC 2020, actualización 2021-12-27).
            </span>
          </label>
        </div>
      </Card>

      {/* Modelos (unidad de análisis del servicio) */}
      <Card title="Modelos para la opinión técnica">
        <p className="mb-3 text-sm text-fg-subtle">
          Captura de 1 a {maxModelos} modelos; cada renglón requiere marca, modelo y
          fabricante. Si necesitas más de {maxModelos}, genera otra solicitud.
        </p>
        <div className="space-y-3">
          {modelos.map((m, idx) => (
            <div
              key={idx}
              className="grid items-end gap-2 rounded-md border border-border p-3 sm:grid-cols-[auto_1fr_1fr_1fr_auto]"
            >
              <span className="pb-2 text-sm font-medium text-fg-subtle">{idx + 1}.</span>
              <FormField label="Marca" required>
                <Input value={m.marca} onChange={(e) => setModelo(idx, "marca", e.target.value)} />
              </FormField>
              <FormField label="Modelo" required>
                <Input value={m.modelo} onChange={(e) => setModelo(idx, "modelo", e.target.value)} />
              </FormField>
              <FormField label="Fabricante" required>
                <Input value={m.fabricante} onChange={(e) => setModelo(idx, "fabricante", e.target.value)} />
              </FormField>
              <Button
                variant="ghost"
                aria-label={`Eliminar modelo ${idx + 1}`}
                icon={<Trash2 className="h-4 w-4" />}
                disabled={modelos.length === 1 || enviando}
                onClick={() => quitarModelo(idx)}
              />
            </div>
          ))}
        </div>
        {modelosDuplicados.length > 0 && (
          <Alert variant="error" className="mt-3">
            Modelos duplicados en la solicitud: {modelosDuplicados.join(", ")}. Cada tupla
            marca + modelo debe capturarse una sola vez.
          </Alert>
        )}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-fg-subtle">
            {modelos.length} / {maxModelos} modelos
          </span>
          <Button
            variant="secondary"
            icon={<Plus className="h-4 w-4" />}
            disabled={modelos.length >= maxModelos || enviando}
            onClick={() => setModelos([...modelos, { ...MODELO_VACIO }])}
          >
            Agregar otro modelo
          </Button>
        </div>
      </Card>

      {/* Documentación (requisitos NPM-T0039) */}
      <Card title="Documentación del producto">
        <Alert variant="info" title="Requisitos NPM-T0039">
          Adjunta las <strong>especificaciones técnicas</strong>, el <strong>instructivo o
          manual</strong> y <strong>folletos, fotografías o imágenes</strong> que permitan
          identificar el producto. Toda la documentación debe estar en idioma español; el
          diagrama es lo único que puede presentarse en inglés.
        </Alert>
        <div className="mt-4">
          <CapturaDocumentos
            tag="TELECOM_OPINION_TECNICA"
            titulo="Documentos de la solicitud"
            ayuda="Los documentos aplican a todos los modelos capturados."
            docs={docs}
            onChange={setDocs}
            disabled={enviando}
          />
        </div>
        {requeridosPendientes.length > 0 && (
          <Alert variant="warning" className="mt-3">
            Aún no adjuntas: {requeridosPendientes.join(" · ")}. Puedes enviar la solicitud,
            pero NPMX los requerirá durante la evaluación (NPM-T0039).
          </Alert>
        )}
        <FormField label="Observaciones del usuario" className="mt-4">
          <Textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Comentarios para NPMX (opcional)."
          />
        </FormField>
      </Card>

      {/* Declaración y firma */}
      <Card title="Representante autorizado y declaración">
        {/* RF-005: se propone desde el registro legal vigente del titular; solo
            si no hay responsables registrados se habilita la captura libre. */}
        <ResponsablePicker
          label="Nombre del representante autorizado"
          registroLegalId={titular?.id}
          value={representante}
          onChange={setRepresentante}
          required
        />
        <div className="mt-4 rounded-md border border-border bg-surface-2 p-3 text-sm italic">
          {DECLARACION}
        </div>
        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={declaracion}
            onChange={(e) => setDeclaracion(e.target.checked)}
          />
          <span>
            Acepto la declaración bajo protesta de decir verdad a nombre del representante
            autorizado. La solicitud será atendida sólo si los requisitos anexos están
            completos.
          </span>
        </label>
      </Card>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex justify-end gap-2">
        <Link to="/telecom/servicios">
          <Button variant="secondary" disabled={enviando}>Regresar</Button>
        </Link>
        <Button
          variant="success"
          icon={<Send className="h-4 w-4" />}
          loading={enviando}
          disabled={!valido || enviando}
          onClick={enviar}
        >
          Enviar solicitud
        </Button>
      </div>
    </PageContainer>
  );
}
